import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ChatWidget from 'components/chat/ChatWidget/ChatWidget';
import { isAppChannelParam } from 'utils/embedParams';
import { notifyEmbedParent, parseEmbedMessage, isAllowedEmbedParentMessage } from 'utils/embedMessaging';
import './EmbedPage.scss';

type EmbedMode = 'window' | 'inline' | 'fullscreen';

const EmbedPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = (searchParams.get('mode') || 'inline') as EmbedMode;
    const channel = searchParams.get('channel') || 'web';
    const isAppChannel = isAppChannelParam(channel);
    const showWelcomeInWindowMode = mode === 'window' && !isAppChannel;

    const [isHomeScreen, setIsHomeScreen] = useState(showWelcomeInWindowMode);

    const embedClassName = useMemo(
        () => `embed-page embed-page--${mode}`,
        [mode]
    );

    const closeWidget = useCallback(() => {
        notifyEmbedParent('close');
    }, []);

    const handleBackToWelcome = useCallback((value: boolean) => {
        setIsHomeScreen(value);
    }, []);

    useEffect(() => {
        if (mode !== 'window' && mode !== 'fullscreen') {
            return undefined;
        }

        document.documentElement.classList.add('mtn-embed-window');
        document.body.classList.add('mtn-embed-window');
        document.documentElement.style.height = '100%';

        return () => {
            document.documentElement.classList.remove('mtn-embed-window');
            document.body.classList.remove('mtn-embed-window');
            document.documentElement.style.height = '';
        };
    }, [mode]);

    useEffect(() => {
        notifyEmbedParent('ready', { channel, mode });
    }, [channel, mode]);

    useEffect(() => {
        const handleParentMessage = (event: MessageEvent) => {
            if (!isAllowedEmbedParentMessage(event.origin)) {
                return;
            }

            const parsed = parseEmbedMessage(event.data);
            if (!parsed) {
                return;
            }

            if (parsed.event === 'update') {
                const nextParams = new URLSearchParams(searchParams.toString());
                Object.entries(parsed.payload).forEach(([key, value]) => {
                    if (value === undefined || value === null || value === '') {
                        nextParams.delete(key);
                    } else {
                        nextParams.set(key, String(value));
                    }
                });
                setSearchParams(nextParams, { replace: true });
            }
        };

        window.addEventListener('message', handleParentMessage);
        return () => window.removeEventListener('message', handleParentMessage);
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!isHomeScreen) {
            notifyEmbedParent('open', { channel, mode });
        }
    }, [channel, isHomeScreen, mode]);

    return (
        <div className={embedClassName}>
            <ChatWidget
                closeWidget={closeWidget}
                isHomeScreen={isHomeScreen}
                setIsHomeScreen={handleBackToWelcome}
                hideHeader={mode === 'inline' || isAppChannel}
            />
        </div>
    );
};

export default EmbedPage;
